import React from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * Section "Informations Générales" - Mode Professionnel uniquement.
 * Affiche les champs Client / Commercial puis les champs spécifiques Élec ou Gaz.
 */
const InformationsGenerales = ({
  energyType,
  // Champs communs
  clientName,
  setClientName,
  commercialName,
  setCommercialName,
  // Électricité
  puissanceSouscriteInput,
  setPuissanceSouscriteInput,
  isPuissanceValide,
  puissanceAtteinteInput,
  setPuissanceAtteinteInput,
  segmentElec,
  setSegmentElec,
  optionTarifaire,
  setOptionTarifaire,
  nafCode,
  setNafCode,
  // Gaz
  segmentGaz,
  setSegmentGaz,
  isNafIndustrielGaz,
  capaciteJournaliereT4,
  setCapaciteJournaliereT4,
  niveauCapaciteT4,
  setNiveauCapaciteT4,
}) => {
  return (
    <section className="card" data-testid="info-section">
      <h2 className="section-title">Informations Générales</h2>
      <div className="form-grid form-grid-2">
        <div className="form-group">
          <label htmlFor="clientName">Nom du Client</label>
          <input
            type="text"
            id="clientName"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Entrez le nom du client"
            data-testid="client-name-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="commercialName">Nom du Commercial</label>
          <input
            type="text"
            id="commercialName"
            value={commercialName}
            onChange={(e) => setCommercialName(e.target.value)}
            placeholder="Entrez le nom du commercial"
            data-testid="commercial-name-input"
          />
        </div>
      </div>

      {/* Champs spécifiques Électricité */}
      {energyType === 'electricity' && (
        <>
          <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
            <div className="form-group">
              <label htmlFor="puissanceSouscrite">Puissance Souscrite (kVA)</label>
              <input
                type="number"
                id="puissanceSouscrite"
                value={puissanceSouscriteInput}
                onChange={(e) => setPuissanceSouscriteInput(e.target.value)}
                placeholder="3 - 10 000 kVA"
                min="3"
                max="10000"
                step="1"
                data-testid="puissance-souscrite-input"
              />
              {puissanceSouscriteInput && !isPuissanceValide && (
                <span className="error-message">
                  <AlertTriangle size={14} />
                  La puissance doit être entre 3 et 10 000 kVA
                </span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="puissanceAtteinte">Puissance Atteinte (kVA)</label>
              <input
                type="number"
                id="puissanceAtteinte"
                value={puissanceAtteinteInput}
                onChange={(e) => setPuissanceAtteinteInput(e.target.value)}
                placeholder="kVA"
                min="0"
                step="1"
                data-testid="puissance-atteinte-input"
              />
            </div>
          </div>
          <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
            <div className="form-group">
              <label htmlFor="segmentElec">Segment Client</label>
              <select
                id="segmentElec"
                value={segmentElec}
                onChange={(e) => setSegmentElec(e.target.value)}
                data-testid="segment-elec-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="C5">C5</option>
                <option value="C4">C4</option>
                <option value="C3">C3</option>
                <option value="C2">C2</option>
                <option value="C1">C1</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="optionTarifaire">Option Tarifaire</label>
              <select
                id="optionTarifaire"
                value={optionTarifaire}
                onChange={(e) => setOptionTarifaire(e.target.value)}
                data-testid="option-tarifaire-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="CU">CU - Courte Utilisation</option>
                <option value="MU">MU - Moyenne Utilisation</option>
                <option value="LU">LU - Longue Utilisation</option>
              </select>
            </div>
          </div>
          <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
            <div className="form-group">
              <label htmlFor="nafCode">Code NAF</label>
              <input
                type="text"
                id="nafCode"
                value={nafCode}
                onChange={(e) => setNafCode(e.target.value)}
                placeholder="Ex: 25.62A"
                data-testid="naf-code-input"
              />
            </div>
          </div>
        </>
      )}

      {/* Champs spécifiques Gaz */}
      {energyType === 'gas' && (
        <>
          <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
            <div className="form-group">
              <label htmlFor="segmentGaz">Segment Gaz</label>
              <select
                id="segmentGaz"
                value={segmentGaz}
                onChange={(e) => setSegmentGaz(e.target.value)}
                data-testid="segment-gaz-select"
              >
                <option value="T1">T1 - Résidentiel (≤ 6 MWh/an)</option>
                <option value="T2">T2 - Petit professionnel (6 - 300 MWh/an)</option>
                <option value="T3">T3 - Professionnel (300 - 5000 MWh/an)</option>
                <option value="T4">T4 - Industriel (&gt; 5000 MWh/an)</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="nafCodeGaz">Code NAF</label>
              <input
                type="text"
                id="nafCodeGaz"
                value={nafCode}
                onChange={(e) => setNafCode(e.target.value)}
                placeholder="Ex: C25.62A"
                data-testid="naf-code-gaz-input"
              />
              {nafCode && (
                <span className={`info-message ${isNafIndustrielGaz ? 'success' : ''}`}>
                  {isNafIndustrielGaz ? (
                    <><CheckCircle size={14} /> Éligible taux réduit accise</>
                  ) : (
                    <><AlertTriangle size={14} /> Taux standard accise</>
                  )}
                </span>
              )}
            </div>
          </div>
          {/* Capacité journalière pour T4 */}
          {segmentGaz === 'T4' && (
            <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label htmlFor="capaciteT4">Capacité Journalière (MWh/j)</label>
                <input
                  type="number"
                  id="capaciteT4"
                  value={capaciteJournaliereT4}
                  onChange={(e) => setCapaciteJournaliereT4(e.target.value)}
                  placeholder="MWh/jour"
                  min="0"
                  step="0.1"
                  data-testid="capacite-t4-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="niveauCapacite">Niveau de Capacité</label>
                <select
                  id="niveauCapacite"
                  value={niveauCapaciteT4}
                  onChange={(e) => setNiveauCapaciteT4(e.target.value)}
                  data-testid="niveau-capacite-select"
                >
                  <option value="T4_low">Niveau bas (144 €/MWh/j)</option>
                  <option value="T4_high">Niveau haut (288 €/MWh/j)</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default InformationsGenerales;
